import type {
  ApiErrorCode,
  ApiResult,
  AuthTokenProvider,
  NativeUploadFile,
  RequestOptions,
  UploadFileInput,
} from '../types/api';
import { clearAuthSession, getStoredAccessToken } from '../utils/authSession';
import { RUNTIME_API_BASE_URL } from '../config/runtimeApi';
import { resolveRuntimeApiUrl } from '../utils/runtimeApiSupport';

export { getStoredAccessToken };

type ApiClientConfig = {
  baseUrl: string;
  getToken?: AuthTokenProvider;
  timeoutMs?: number;
};

const DEFAULT_REQUEST_TIMEOUT_MS = 20000;

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  if (timeoutMs <= 0) {
    return promise;
  }
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => reject(new ApiError('接口请求超时，请稍后重试', 'network')), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export class ApiError extends Error {
  readonly code: ApiErrorCode;

  readonly status: number | null;

  constructor(message: string, code: ApiErrorCode, status: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

const joinUrl = (baseUrl: string, path: string): string =>
  resolveRuntimeApiUrl(baseUrl, path);

const toErrorMessage = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  if (record.detail && typeof record.detail === 'object') {
    const detail = record.detail as Record<string, unknown>;
    if (typeof detail.message === 'string' && detail.message.trim()) {
      return detail.message.trim();
    }
  }
  if (typeof record.detail === 'string' && record.detail.trim()) {
    return record.detail.trim();
  }
  if (record.error && typeof record.error === 'object') {
    const error = record.error as Record<string, unknown>;
    if (typeof error.message === 'string') {
      return error.message;
    }
  }
  if (typeof record.message === 'string') {
    return record.message;
  }
  if (typeof record.error === 'string') {
    return record.error;
  }
  return null;
};

const toErrorCode = (value: unknown, status: number): ApiErrorCode => {
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (record.detail && typeof record.detail === 'object') {
      const detail = record.detail as Record<string, unknown>;
      if (typeof detail.code === 'string') {
        return detail.code as ApiErrorCode;
      }
    }
    if (record.error && typeof record.error === 'object') {
      const error = record.error as Record<string, unknown>;
      if (typeof error.code === 'string') {
        return error.code as ApiErrorCode;
      }
    }
  }

  if (status === 401) {
    return 'unauthorized';
  }
  if (status >= 500) {
    return 'server';
  }
  return 'unknown';
};

const canCreateBrowserFileFromUri = (file: UploadFileInput): boolean =>
  Boolean(
    file.uri &&
      typeof fetch === 'function' &&
      typeof Blob !== 'undefined' &&
      typeof File !== 'undefined' &&
      /^(blob:|data:)/i.test(file.uri)
  );

const createBrowserFileFromUri = async (file: UploadFileInput): Promise<File> => {
  const response = await fetch(file.uri);
  if (!response.ok) {
    throw new ApiError(`Unable to read selected image: ${response.status}`, 'network');
  }

  const blob = await response.blob();
  const fileName = file.fileName ?? `photo-search-${Date.now()}.jpg`;
  const mimeType = file.mimeType ?? blob.type ?? 'image/jpeg';
  return new File([blob], fileName, { type: mimeType });
};

export const appendUploadFileToFormData = async (
  formData: FormData,
  fieldName: string,
  file: UploadFileInput
): Promise<void> => {
  if (file.file) {
    formData.append(fieldName, file.file, file.fileName ?? undefined);
    return;
  }

  if (canCreateBrowserFileFromUri(file)) {
    formData.append(fieldName, await createBrowserFileFromUri(file));
    return;
  }

  const nativeFile: NativeUploadFile = {
    uri: file.uri,
    name: file.fileName ?? `photo-search-${Date.now()}.jpg`,
    type: file.mimeType ?? 'image/jpeg',
  };

  formData.append(fieldName, nativeFile as unknown as Blob);
};

export const createApiClient = ({ baseUrl, getToken, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS }: ApiClientConfig) => {
  const request = async <TResponse, TBody = unknown>(
    path: string,
    options: RequestOptions<TBody> = {}
  ): Promise<ApiResult<TResponse>> => {
    const requestTimeoutMs = options.timeoutMs ?? timeoutMs;
    const token = getToken ? await getToken() : null;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...options.headers,
    };

    let body: RequestInit['body'] | undefined;
    if (options.formData) {
      body = options.formData;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    let response: Response;
    const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId =
      abortController && requestTimeoutMs > 0
        ? setTimeout(() => abortController.abort(), requestTimeoutMs)
        : null;

    try {
      response = await withTimeout(
        fetch(joinUrl(baseUrl, path), {
          method: options.method ?? 'GET',
          headers,
          body,
          signal: abortController?.signal,
        }),
        requestTimeoutMs
      );
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        throw error;
      }
      const message =
        error instanceof Error && error.name === 'AbortError'
          ? '接口请求超时，请稍后重试'
          : '网络异常，请稍后重试';
      throw new ApiError(message, 'network');
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }

    let payload: unknown = null;
    try {
      const rawText = await response.text();
      payload = rawText ? JSON.parse(rawText) : null;
    } catch (error: unknown) {
      throw new ApiError('接口返回格式异常', 'parse', response.status);
    }

    if (!response.ok) {
      const message = toErrorMessage(payload) ?? '接口请求失败';
      const code = toErrorCode(payload, response.status);
      if (response.status === 401) {
        await clearAuthSession();
      }
      throw new ApiError(message, code, response.status);
    }

    return {
      data: payload as TResponse,
      status: response.status,
    };
  };

  return {
    request,
    get: <TResponse>(path: string): Promise<ApiResult<TResponse>> => request<TResponse>(path),
    post: <TResponse, TBody = unknown>(
      path: string,
      body: TBody,
      options: Omit<RequestOptions<TBody>, 'method' | 'body' | 'formData'> = {}
    ): Promise<ApiResult<TResponse>> =>
      request<TResponse, TBody>(path, { ...options, method: 'POST', body }),
    postForm: <TResponse>(
      path: string,
      formData: FormData
    ): Promise<ApiResult<TResponse>> =>
      request<TResponse>(path, { method: 'POST', formData }),
  };
};

export const apiClient = createApiClient({
  baseUrl: RUNTIME_API_BASE_URL,
  getToken: getStoredAccessToken,
});
